import { useCallback, useMemo, useState } from "react";

/** Generate a unique key for a row */
export const makeKey = () =>
  `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** Ensure each row has a key */
export function ensureKeys(rows, keyField = "_key", keyFactory = makeKey) {
  return rows.map((r) => (r && r[keyField] ? r : { ...r, [keyField]: keyFactory() }));
}

/** Generic comparator for number/string/boolean; empty values sort last in asc */
export function defaultCompare(a, b) {
  const A = a ?? "";
  const B = b ?? "";
  const aNum = typeof A === "number" && Number.isFinite(A);
  const bNum = typeof B === "number" && Number.isFinite(B);
  if (aNum && bNum) return A - B;
  return String(A).localeCompare(String(B));
}

/** Build a comparator from column definition and direction */
export function buildComparator(columns, by, dir = "asc") {
  const col = columns.find((c) => c.id === by);
  if (!col) return () => 0;

  const accessor = col.accessor || ((row) => row[col.id]);
  const cmpBase = col.comparator || defaultCompare;
  const mul = dir === "desc" ? -1 : 1;

  return (a, b) => {
    const res = cmpBase(accessor(a.row), accessor(b.row));
    if (res !== 0) return res * mul;
    return a.index - b.index;
  };
}

/** Basic global filter over columns marked as `searchable: true` */
export function filterRows(rows, query, columns) {
  const q = (query || "").toLowerCase().trim();
  if (!q) return rows;

  const searchable = columns.filter((c) => c.searchable);
  if (searchable.length === 0) return rows;

  return rows.filter((row) =>
    searchable.some((c) => {
      const accessor = c.accessor || ((r) => r[c.id]);
      const filterValue =
        c.filterValue ||
        ((v, r) => (v === true ? "yes" : v === false ? "no" : String(v ?? "")));
      const val = filterValue(accessor(row), row);
      return String(val).toLowerCase().includes(q);
    })
  );
}

/** Apply sort and return the row keys in the new order */
export function sortToOrder(rows, columns, by, dir, keyField = "_key") {
  if (!by) return rows.map((r) => r[keyField]);
  const withIndex = rows.map((row, index) => ({ row, index }));
  const cmp = buildComparator(columns, by, dir);
  withIndex.sort(cmp);
  return withIndex.map((x) => x.row[keyField]);
}

/** Order rows according to `rowOrder` */
export function orderRows(rows, rowOrder, keyField = "_key") {
  const idx = new Map(rowOrder.map((k, i) => [k, i]));
  return [...rows].sort(
    (a, b) => (idx.get(a[keyField]) ?? 0) - (idx.get(b[keyField]) ?? 0)
  );
}

/** Build a JSON signature; caller may pass a normalizer to strip keys/order */
export function signature(rows, normalizeFn) {
  return JSON.stringify(normalizeFn ? normalizeFn(rows) : rows);
}

/**
 * Headless table model hook.
 * columns: [{ id, accessor?, comparator?, searchable?, filterValue? }]
 *
 * Filter behavior: results are "pinned" at the moment the filter text changes.
 * Editing cell values does NOT change the filtered set until the user either
 * changes the filter again or commits (Save).
 */
export function useTableModel({
                                initialRows = [],
                                columns = [],
                                keyField = "_key",
                                initialSortBy = null,
                                initialSortDir = "asc",
                                normalizeForSignature,
                              }) {
  const [rows, setRowsState] = useState(() => ensureKeys(initialRows, keyField));
  const [rowOrder, setRowOrder] = useState(() =>
    sortToOrder(rows, columns, initialSortBy, initialSortDir, keyField)
  );
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortDir, setSortDir] = useState(initialSortDir);

  const [baselineSig, setBaselineSig] = useState("");

  const [filter, setFilterState] = useState("");
  const [pinnedKeys, setPinnedKeys] = useState(null);

  const setRows = useCallback(
    (next) => {
      const withKeys = ensureKeys(next, keyField);
      setRowsState(withKeys);
      setRowOrder(sortToOrder(withKeys, columns, sortBy, sortDir, keyField));

      if (filter) {
        const pinned = filterRows(withKeys, filter, columns).map((r) => r[keyField]);
        setPinnedKeys(new Set(pinned));
      }
    },
    [columns, keyField, sortBy, sortDir, filter]
  );

  const addRow = useCallback(
    (row) => {
      const newRow = row[keyField] ? row : { ...row, [keyField]: makeKey() };
      setRowsState((prev) => [newRow, ...prev]);
      setRowOrder((prev) => [newRow[keyField], ...prev]);
      // NOTE: do NOT recompute pinnedKeys here; keep the filtered set locked while editing.
    },
    [keyField]
  );

  const updateRow = useCallback(
    (key, patch) => {
      setRowsState((prev) =>
        prev.map((r) => (r[keyField] === key ? { ...r, ...patch } : r))
      );
      // NOTE: do NOT recompute pinnedKeys here; keep the filtered set locked while editing.
    },
    [keyField]
  );

  const removeRow = useCallback(
    (key) => {
      setRowsState((prev) => prev.filter((r) => r[keyField] !== key));
      setRowOrder((prev) => prev.filter((k) => k !== key));
      setPinnedKeys((prev) => {
        if (!prev) return prev;
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    },
    [keyField]
  );

  const toggleSort = useCallback(
    (columnId) => {
      let nextBy = columnId;
      let nextDir = "asc";
      if (sortBy === columnId) nextDir = sortDir === "asc" ? "desc" : "asc";
      setSortBy(nextBy);
      setSortDir(nextDir);
      setRowOrder(() => sortToOrder(rows, columns, nextBy, nextDir, keyField));
    },
    [rows, columns, keyField, sortBy, sortDir]
  );

  const applyCurrentSort = useCallback(() => {
    setRowOrder(sortToOrder(rows, columns, sortBy, sortDir, keyField));
  }, [rows, columns, keyField, sortBy, sortDir]);

  const setBaselineFromRows = useCallback(
    (baselineRows) => {
        setBaselineSig(signature(baselineRows, normalizeForSignature));
      if (filter) {
        const pinned = filterRows(baselineRows, filter, columns).map((r) => r[keyField]);
        setPinnedKeys(new Set(pinned));
      }
    },
    [normalizeForSignature, filter, columns, keyField]
  );

  const setFilter = useCallback(
    (query) => {
      const q = query || "";
      setFilterState(q);
      if (!q.trim()) {
        setPinnedKeys(null); // no filter => show all
      } else {
        const pinned = filterRows(rows, q, columns).map((r) => r[keyField]);
        setPinnedKeys(new Set(pinned));
      }
    },
    [rows, columns, keyField]
  );

  const canSave = useMemo(() => {
    if (!baselineSig) return false;
    return signature(rows, normalizeForSignature) !== baselineSig;
  }, [rows, baselineSig, normalizeForSignature]);

  const filteredRows = useMemo(() => {
    if (!filter || !pinnedKeys) return rows;
    return rows.filter((r) => pinnedKeys.has(r[keyField]));
  }, [rows, filter, pinnedKeys, keyField]);

  const visibleRows = useMemo(
    () => orderRows(filteredRows, rowOrder, keyField),
    [filteredRows, rowOrder, keyField]
  );

  return {
    // state
    rows,
    rowOrder,
    visibleRows,
    filter,
    sortBy,
    sortDir,
    canSave,
    // actions
    setRows,
    setFilter,
    toggleSort,
    addRow,
    updateRow,
    removeRow,
    applyCurrentSort,
    setBaselineFromRows,
    // helpers
    keyField,
  };
}
